import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useI18n } from "../../lib/i18n.jsx";

export default function ProductDetail({ product, open, onClose, onRequestQuote, onAskFlora }) {
  const { t, lang } = useI18n();
  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 bg-[#f7f7f4] border-[#e5e8e2] rounded-2xl"
        data-testid="product-detail-modal"
      >
        <DialogTitle className="sr-only">{product.name[lang]}</DialogTitle>
        <DialogDescription className="sr-only">{product.tagline[lang]}</DialogDescription>
        <div className="grid lg:grid-cols-12 gap-0">
          <div className="lg:col-span-5 bg-white p-8 lg:p-12 flex flex-col justify-center eu-product-bg relative">
            <div className="absolute top-6 left-6 font-mono-eu text-[10px] font-bold tracking-wider bg-[#133322] text-[#f7f7f4] px-3 py-1.5 rounded-full">
              {product.npk}
            </div>
            <img
              src={product.image}
              alt={product.name[lang]}
              className="w-full max-w-xs mx-auto object-contain"
            />
          </div>

          <div className="lg:col-span-7 p-8 lg:p-12">
            <div className="eu-data-label mb-3">{product.category[lang]}</div>
            <h3 className="font-display text-3xl lg:text-4xl text-[#133322] leading-tight">
              {product.name[lang]}
            </h3>
            <p className="mt-3 text-[#5c6b61]">{product.tagline[lang]}</p>
            <p className="mt-5 text-sm text-[#1c1c1c] leading-relaxed">
              {product.description[lang]}
            </p>

            <div className="mt-8">
              <div className="eu-data-label mb-4">{t.productDetail.benefits}</div>
              <ul className="grid sm:grid-cols-2 gap-2">
                {product.benefits[lang].map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <i className="fa-solid fa-check text-[#b45f3e] mt-1 text-xs"></i>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <div className="eu-data-label mb-4">{t.productDetail.composition}</div>
              <div className="grid grid-cols-3 gap-2">
                {product.composition.map((c, i) => (
                  <div
                    key={i}
                    className="border border-[#e5e8e2] rounded-xl p-3 bg-white"
                  >
                    <div className="font-mono-eu text-[10px] text-[#5c6b61] uppercase">
                      {c.label}
                    </div>
                    <div className="font-mono-eu text-sm font-bold text-[#133322] mt-1">
                      {c.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="border border-[#e5e8e2] rounded-xl p-4 bg-white">
                <div className="eu-data-label">{t.productDetail.foliar}</div>
                <div className="mt-2 text-sm font-medium text-[#133322]">
                  {product.dosage.foliar[lang]}
                </div>
              </div>
              <div className="border border-[#e5e8e2] rounded-xl p-4 bg-white">
                <div className="eu-data-label">{t.productDetail.fertigation}</div>
                <div className="mt-2 text-sm font-medium text-[#133322]">
                  {product.dosage.fertigation[lang]}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="eu-data-label mb-3">{t.productDetail.crops}</div>
              <div className="flex flex-wrap gap-2">
                {product.crops[lang].map((c, i) => (
                  <span
                    key={i}
                    className="text-xs bg-[#9ead9b]/15 text-[#133322] px-3 py-1.5 rounded-full"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={() => onRequestQuote(product)}
                className="eu-btn-primary"
                data-testid="modal-request-quote"
              >
                <i className="fa-solid fa-paper-plane text-xs"></i>
                {t.productDetail.request_quote}
              </button>
              <button
                onClick={() => onAskFlora(product)}
                className="eu-btn-outline"
                data-testid="modal-ask-flora"
              >
                <i className="fa-solid fa-comment-dots text-xs"></i>
                {t.productDetail.ask_flora}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
